<script lang="ts" setup>
    import CodeEdit from '@/shared/codeedit/CodeEdit.vue';
    import TabLayout from './TabLayout.vue';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import { usePipeClassStore } from '@/stores/pipeClassStore.ts';
    import { computed, ref, watch } from 'vue';
    import { requireCanEditCode } from '@/shared/directives/requirePermission';
    import { encodeIdBase64 } from '@/shared/helpers/utils';

    const vRequireCanEditCode = requireCanEditCode;

    const formKey = ref<number>(0);

    const isFormActive = ref<boolean>(false);
    const pipeClassStore = usePipeClassStore();

    const isEditing = computed(() => {
        return isFormActive.value && !!pipeClassStore.selectedLineSize;
    });

    const codeEditTitle = computed(() => {
        const verb = isEditing.value ? 'Edit' : 'New';
        return verb + ' Line Size';
    });

    const isCreating = computed(() => {
        return isFormActive.value && !pipeClassStore.selectedLineSize;
    });

    const existsCodeSetForScope = computed(() => {
        return !pipeClassStore.loading.lineSizeCodeSetName && pipeClassStore.currentLineSizeCodeSetName !== null;
    });

    watch(() => pipeClassStore.selectedScope, () => {
        pipeClassStore.setLineSize(null);
        isFormActive.value = false;
    });

    watch(() => pipeClassStore.selectedLineSize, (newValue, oldValue) => {
        if (newValue) {
            isFormActive.value = true;
        } else if (!oldValue) {
            isFormActive.value = false;
        }
        formKey.value++;
    });

    function onNewLineSizeClicked() {
        isFormActive.value = true;
        pipeClassStore.setLineSize(null);
    }

    function onCancelClicked() {
        pipeClassStore.setLineSize(null);
        isFormActive.value = false;
    }

    async function onSavedLineSize() {
        const newSizes = await pipeClassStore.loadLineSizes(true, true);
        // picks the first new entry from the load operation as the current line size
        // this is so that the newly created line size is opened in code-edit
        if (newSizes && newSizes.length > 0) {
            pipeClassStore.setLineSize(newSizes[0].Id);
        }
        formKey.value++;
    }
</script>
<template>
    <tab-layout
        :has-selected-scope="!!pipeClassStore.selectedScope"
        :is-form-active="isFormActive"
        :exists-code-set-for-scope="existsCodeSetForScope"
        :code-edit-title="codeEditTitle"
        one-third>
        <template #toolbar>
            <div
                v-require-can-edit-code="{ libraryName: 'LineSize', scopes: pipeClassStore.selectedScope ? [pipeClassStore.selectedScope] : [] }"
                class="level has-margin-bottom">
                <div
                    class="level-left">
                    <b-button
                        class="level-item"
                        type="is-info"
                        :disabled="!pipeClassStore.selectedScope"
                        :model-value="isCreating"
                        @click="onNewLineSizeClicked">
                        <span class="icon is-small">
                            <i
                                class="mdi mdi-plus"
                                aria-hidden="true" />
                        </span>
                        <span>
                            New Line Size
                        </span>
                    </b-button>
                </div>
            </div>
        </template>
        <template #picker>
            <b-table
                v-model:selected="pipeClassStore.selectedLineSize"
                class="is-flex"
                height="60vh"
                sticky-header
                narrowed
                striped
                hoverable
                bordered
                :data="pipeClassStore.lineSizes"
                :loading="pipeClassStore.loading.lineSizes"
                :mobile-cards="false">
                <b-table-column
                    field="Name"
                    label="Nominal diameter"
                    width="100px">
                    <template #default="{ row }">
                        {{ row.Name }}
                    </template>
                </b-table-column>
                <b-table-column
                    field="NomDiamUnit"
                    label="Unit"
                    width="110px"
                    sortable>
                    <template #default="{ row }">
                        {{ row.NomDiamUnit }}
                    </template>
                </b-table-column>
                <b-table-column
                    v-slot="slotProps"
                    sortable
                    centered
                    field="IsValid"
                    label="Valid">
                    <bool-element :value="slotProps.row.IsValid" />
                </b-table-column>
                <template #empty>
                    <div class="has-text-centered">
                        No sizes
                    </div>
                </template>
            </b-table>
        </template>
        <template #form>
            <code-edit
                :key="formKey"
                library="LineSize"
                :scopes="pipeClassStore.selectedScope ? [pipeClassStore.selectedScope] : undefined"
                :code-id="pipeClassStore.selectedLineSize ? encodeIdBase64('Code', pipeClassStore.selectedLineSize.Id) : undefined"
                :code-set-name="pipeClassStore.currentLineSizeCodeSetName ?? undefined"
                :fields-to-ignore="['attachmentKey']"
                :custom-labels="{ name: 'Nominal diameter' }"
                :use-box-render="false"
                use-columns
                @cancel="onCancelClicked"
                @refresh="onSavedLineSize" />
        </template>
    </tab-layout>
</template>
<style scoped>

</style>
